import { SITE, aggregateRating as siteAggregateRating, activeSocials } from '../data/site';

/**
 * Schema.org structured data generators
 * All values are derived from the SITE config - no hardcoded office-specific data
 */

function sameAsUrls() {
  return [
    ...activeSocials.map((s) => s.url),
    ...(SITE.googlePlaceId
      ? [`https://www.google.com/maps/place/?q=place_id:${SITE.googlePlaceId}`]
      : []),
    ...SITE.directoryProfiles,
  ];
}

/**
 * Business description plus the Saturday note. Recurring hours stay separate
 * from the dated Saturday exceptions in openingHoursSpecification.
 */
function businessDescription() {
  return `${SITE.description} ${SITE.hours.schemaNote}`;
}

function specialOpeningHoursSpecification() {
  return [
    {
      '@type': 'OpeningHoursSpecification',
      validFrom: '2026-11-26',
      validThrough: '2026-11-26',
      opens: '00:00',
      closes: '00:00',
    },
  ];
}

type Doctor = typeof SITE.doctors[number];

function normalizePersonName(name: string) {
  return name.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
}

function resolveSchemaDoctor(input: {
  author?: string;
  doctorIndex?: number;
  doctorSchemaId?: string;
}): Doctor | undefined {
  if (input.doctorSchemaId) {
    return SITE.doctors.find((doctor) => doctor.schemaId === input.doctorSchemaId);
  }

  if (typeof input.doctorIndex === 'number') {
    return SITE.doctors[input.doctorIndex];
  }

  if (input.author) {
    const authorName = normalizePersonName(input.author);
    return SITE.doctors.find((doctor) => {
      const names = [
        doctor.fullName,
        `${doctor.firstName} ${doctor.lastName}`,
        doctor.lastName,
      ];
      return names.some((name) => normalizePersonName(name) === authorName);
    });
  }

  return SITE.doctor;
}

function authorSchema(authorName: string, doctor: Doctor | undefined) {
  if (doctor) {
    return {
      '@type': 'Person',
      name: authorName,
      '@id': `https://www.${SITE.domain}/#${doctor.schemaId}`,
      jobTitle: doctor.title,
      hasCredential: doctor.honorificSuffix,
    };
  }

  if (authorName === SITE.name || authorName === SITE.shortName) {
    return {
      '@type': 'Organization',
      '@id': `https://www.${SITE.domain}/#organization`,
      name: authorName,
    };
  }

  return {
    '@type': 'Person',
    name: authorName,
  };
}

function reviewedOrganizationSchema() {
  return {
    '@type': ['LocalBusiness', 'Chiropractor'],
    '@id': `https://www.${SITE.domain}/#organization`,
    name: SITE.name,
    url: `https://www.${SITE.domain}`,
    telephone: SITE.phone,
    image: `https://www.${SITE.domain}${SITE.images.heroFamily}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
  };
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['MedicalBusiness', 'PhysiciansOffice'],
    '@id': `https://www.${SITE.domain}/#organization`,
    name: SITE.name,
    alternateName: SITE.shortName,
    url: `https://www.${SITE.domain}`,
    logo: `https://www.${SITE.domain}${SITE.images.logo}`,
    image: `https://www.${SITE.domain}${SITE.images.heroFamily}`,
    description: businessDescription(),
    medicalSpecialty: 'Chiropractic',
    isAcceptingNewPatients: true,
    priceRange: SITE.priceRange,
    telephone: SITE.phone,
    email: SITE.email,
    foundingDate: SITE.foundingYear,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE.geo.latitude,
      longitude: SITE.geo.longitude,
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: SITE.geo.latitude,
        longitude: SITE.geo.longitude,
      },
      geoRadius: '30000',
    },
    openingHoursSpecification: [
      ...SITE.hours.structured.map((hours) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: hours.dayOfWeek,
        opens: hours.opens,
        closes: hours.closes,
      })),
      ...SITE.hours.saturdaySpecialDates.map((hours) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: hours.opens,
        closes: hours.closes,
        validFrom: hours.date,
        validThrough: hours.date,
      })),
    ],
    specialOpeningHoursSpecification: specialOpeningHoursSpecification(),
    sameAs: sameAsUrls(),
    hasMap: `https://www.google.com/maps/place/${encodeURIComponent(SITE.name)}/@${SITE.geo.latitude},${SITE.geo.longitude}`,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Chiropractic Services',
      itemListElement: SITE.services.map((service) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'MedicalProcedure',
          name: service.name,
          description: service.description,
        },
      })),
    },
  };
}

function personSchemaForDoctor(doctor: Doctor) {
  return {
    '@context': 'https://schema.org',
    '@type': ['Person', 'IndividualPhysician'],
    '@id': `https://www.${SITE.domain}/#${doctor.schemaId}`,
    name: doctor.fullName,
    givenName: doctor.firstName,
    familyName: doctor.lastName,
    honorificPrefix: doctor.honorificPrefix,
    honorificSuffix: doctor.honorificSuffix,
    jobTitle: doctor.title,
    description: doctor.bio,
    image: `https://www.${SITE.domain}${doctor.image}`,
    url: `https://www.${SITE.domain}${doctor.pageSlug}`,
    ...(doctor.usNPI && { usNPI: doctor.usNPI }),
    medicalSpecialty: 'Chiropractic',
    worksFor: {
      '@id': `https://www.${SITE.domain}/#organization`,
    },
    practicesAt: {
      '@id': `https://www.${SITE.domain}/#organization`,
    },
    alumniOf: {
      '@type': 'CollegeOrUniversity',
      name: doctor.education,
      sameAs: doctor.educationWikidata,
    },
    availableService: doctor.expertise.map((service) => ({
      '@type': 'MedicalProcedure',
      name: service,
    })),
    hasCredential: doctor.certifications.map((cert) => ({
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: cert.type,
      name: cert.name,
    })),
    knowsAbout: doctor.expertise.map((expertise) => {
      const matchingService = SITE.services.find((s) =>
        expertise.toLowerCase().includes(s.shortName.toLowerCase())
      );
      if (matchingService) {
        return {
          '@type': 'Thing',
          name: expertise,
          url: `https://www.${SITE.domain}${matchingService.slug}`,
        };
      }
      return expertise;
    }),
    sameAs: activeSocials
      .filter((s) => ['facebook', 'youtube', 'linkedin'].includes(s.platform))
      .map((s) => s.url),
  };
}

export function personSchema() {
  return SITE.doctors.map(personSchemaForDoctor);
}

export function doctorPersonSchema(doctorSchemaId: string) {
  const doctor = SITE.doctors.find((doctor) => doctor.schemaId === doctorSchemaId);

  if (!doctor) {
    throw new Error(`Unknown doctor schema id: ${doctorSchemaId}`);
  }

  return personSchemaForDoctor(doctor);
}

export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'Chiropractor'],
    '@id': `https://www.${SITE.domain}/#localbusiness`,
    name: SITE.name,
    url: `https://www.${SITE.domain}`,
    logo: `https://www.${SITE.domain}${SITE.images.logo}`,
    image: `https://www.${SITE.domain}${SITE.images.heroFamily}`,
    description: businessDescription(),
    telephone: SITE.phone,
    email: SITE.email,
    isAcceptingNewPatients: true,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE.geo.latitude,
      longitude: SITE.geo.longitude,
    },
    priceRange: SITE.priceRange,
    openingHours: SITE.hours.shortFormat,
    openingHoursSpecification: [
      ...SITE.hours.structured.map((hours) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: hours.dayOfWeek,
        opens: hours.opens,
        closes: hours.closes,
      })),
      ...SITE.hours.saturdaySpecialDates.map((hours) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: hours.opens,
        closes: hours.closes,
        validFrom: hours.date,
        validThrough: hours.date,
      })),
    ],
    specialOpeningHoursSpecification: specialOpeningHoursSpecification(),
    hasMap: `https://www.google.com/maps/place/${encodeURIComponent(SITE.name)}/@${SITE.geo.latitude},${SITE.geo.longitude}`,
    sameAs: sameAsUrls(),
    areaServed: {
      '@type': 'City',
      name: SITE.address.city,
    },
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: `https://www.${SITE.domain}`,
    inLanguage: 'en-US',
    publisher: { '@id': `https://www.${SITE.domain}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `https://www.${SITE.domain}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function videoSchema(video: {
  name: string;
  description: string;
  youtubeId: string;
  uploadDate?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.name,
    description: video.description,
    thumbnailUrl: `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`,
    uploadDate: video.uploadDate || '2024-01-01',
    contentUrl: `https://www.youtube.com/watch?v=${video.youtubeId}`,
    embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
  };
}

export function generateFAQPageSchema(
  faqs: ReadonlyArray<{ readonly question: string; readonly answer: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export const faqSchema = generateFAQPageSchema;

export function articleSchema(article: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  schemaType?: 'Article' | 'BlogPosting' | 'MedicalWebPage';
  author?: string;
  doctorIndex?: number;
  doctorSchemaId?: string;
  url: string;
  reviewedBy?: {
    name: string;
    credentials: string;
  };
  wordCount?: number;
  /** Social-share posts: name the clinic (LocalBusiness, Royal Oak) as the subject of the article. */
  about?: boolean;
}) {
  const doctor = resolveSchemaDoctor(article);
  const authorName = article.author || doctor?.fullName || SITE.doctor.fullName;

  return {
    '@context': 'https://schema.org',
    '@type': article.schemaType || 'MedicalWebPage',
    headline: article.headline,
    description: article.description,
    image: `https://www.${SITE.domain}${article.image}`,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    lastReviewed: article.dateModified,
    author: authorSchema(authorName, doctor),
    ...(article.reviewedBy && {
      reviewedBy: {
        '@type': 'Person',
        name: article.reviewedBy.name,
        jobTitle: article.reviewedBy.credentials,
      },
    }),
    publisher: {
      '@id': `https://www.${SITE.domain}/#organization`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.${SITE.domain}${article.url}`,
    },
    ...(article.wordCount && { wordCount: article.wordCount }),
    ...(article.about && { about: reviewedOrganizationSchema() }),
    inLanguage: 'en-US',
    isAccessibleForFree: true,
  };
}

export function serviceSchema(service: {
  name: string;
  description: string;
  image: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: service.name,
    description: service.description,
    image: `https://www.${SITE.domain}${service.image}`,
    url: `https://www.${SITE.domain}${service.url}`,
    provider: {
      '@id': `https://www.${SITE.domain}/#organization`,
    },
  };
}

export function howToSchema(howTo: {
  name: string;
  description: string;
  image: string;
  totalTime: string;
  steps: Array<{ name: string; text: string }>;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howTo.name,
    description: howTo.description,
    image: `https://www.${SITE.domain}${howTo.image}`,
    totalTime: howTo.totalTime,
    step: howTo.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

export function aggregateRatingSchema(rating?: {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}) {
  // Use provided rating or fall back to computed site rating
  const ratingData = rating || siteAggregateRating;

  return {
    '@context': 'https://schema.org',
    '@type': 'AggregateRating',
    ratingValue: ratingData.ratingValue,
    reviewCount: ratingData.reviewCount,
    bestRating: ratingData.bestRating || 5,
    worstRating: ratingData.worstRating || 1,
    itemReviewed: reviewedOrganizationSchema(),
  };
}

export function reviewSchema(review: {
  author: string;
  datePublished: string;
  reviewBody: string;
  ratingValue: number;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.datePublished,
    reviewBody: review.reviewBody,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.ratingValue,
      bestRating: 5,
      worstRating: 1,
    },
    itemReviewed: reviewedOrganizationSchema(),
  };
}

export function medicalWebPageSchema(page: {
  headline: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified: string;
  author?: string;
  doctorIndex?: number;
  doctorSchemaId?: string;
  url: string;
  condition?: {
    name: string;
    description?: string;
    wikipediaUrl?: string;
    wikidataId?: string;
  };
  therapy?: {
    name: string;
    description: string;
  };
  reviewedBy?: {
    name: string;
    credentials: string;
  };
  wordCount?: number;
}) {
  const doctor = resolveSchemaDoctor(page);
  const authorName = page.author || doctor?.fullName || SITE.doctor.fullName;

  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    headline: page.headline,
    description: page.description,
    image: `https://www.${SITE.domain}${page.image ?? '/images/home-og.webp'}`,
    datePublished: page.datePublished,
    dateModified: page.dateModified,
    lastReviewed: page.dateModified,
    ...(page.condition && {
      about: {
        '@type': 'MedicalCondition',
        name: page.condition.name,
        ...(page.condition.description && { description: page.condition.description }),
        ...(page.condition.wikipediaUrl && { url: page.condition.wikipediaUrl }),
        ...(page.condition.wikidataId && {
          sameAs: `https://www.wikidata.org/wiki/${page.condition.wikidataId}`,
        }),
      },
    }),
    ...(page.therapy && {
      mentions: [
        {
          '@type': 'MedicalTherapy',
          name: page.therapy.name,
          description: page.therapy.description,
          sameAs: 'https://en.wikipedia.org/wiki/Chiropractic',
        },
      ],
    }),
    author: authorSchema(authorName, doctor),
    ...(page.reviewedBy && {
      reviewedBy: {
        '@type': 'Person',
        name: page.reviewedBy.name,
        jobTitle: page.reviewedBy.credentials,
      },
    }),
    publisher: {
      '@id': `https://www.${SITE.domain}/#organization`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.${SITE.domain}${page.url}`,
    },
    ...(page.wordCount && { wordCount: page.wordCount }),
    audience: {
      '@type': 'MedicalAudience',
      audienceType: 'Patient',
    },
    inLanguage: 'en-US',
    isAccessibleForFree: true,
  };
}
